/**
 * Multi-tab: transporte BroadcastChannel + leader persistido (distributedRecovery).
 */

import {
  LEADER_HEARTBEAT_MS,
  LEADER_STALE_MS,
  LEADER_GRACE_MS,
} from './coordination/coordinationConstants';
import { resolveLeaderConflict } from './coordination/conflictResolver';
import { invalidatePreviousOwner } from './coordination/ownershipInvalidation';
import { recordLeaderChange, recordEpochChange } from './telemetry';
import {
  claimLeaderIfStale,
  isPersistedLeader,
  isLeaderStale,
  readLeader,
  releaseLeader,
  renewLeader,
  getLeaderFencingToken,
  getLeaderEpochFromStorage,
} from './coordination/distributedRecovery';
import {
  initTabRegistry,
  destroyTabRegistry,
  broadcastTabDead,
} from './coordination/tabRegistry';

const CHANNEL_NAME = 'gomarke_pixel_queue';

let channel: BroadcastChannel | null = null;
let tabId = '';
let lastRemoteLeaderPing = 0;
let lastLeaderReleaseAt = 0;
let leaderHeartbeatId: ReturnType<typeof setInterval> | null = null;

type TabMessage =
  | { type: 'leader_ping'; tabId: string; at: number }
  | { type: 'leader_claim'; tabId: string; at: number; fencingToken: number }
  | { type: 'leader_release'; tabId: string }
  | { type: 'flush_request' };

export function getTabId(): string {
  return tabId;
}

export function getLeaderEpoch(): number {
  return getLeaderEpochFromStorage();
}

export function getMinLeaderFencingToken(): number {
  return getLeaderFencingToken();
}

export function isLeaderTab(): boolean {
  if (typeof window === 'undefined') return true;
  if (!channel) return true;

  const now = Date.now();
  const persisted = readLeader();

  if (persisted && persisted.expiresAt > now && persisted.tabId === tabId) {
    return true;
  }

  if (now - lastLeaderReleaseAt < LEADER_GRACE_MS && lastLeaderReleaseAt > 0) {
    return claimLeaderIfStale(tabId) && isPersistedLeader(tabId);
  }

  if (isLeaderStale() || now - lastRemoteLeaderPing > LEADER_STALE_MS) {
    return claimLeaderIfStale(tabId) && isPersistedLeader(tabId);
  }

  return false;
}

function broadcastLeaderClaim(): void {
  if (!channel) return;
  const token = getLeaderFencingToken();
  const at = Date.now();
  channel.postMessage({
    type: 'leader_claim',
    tabId,
    at,
    fencingToken: token,
  } satisfies TabMessage);
  channel.postMessage({
    type: 'leader_ping',
    tabId,
    at,
  } satisfies TabMessage);
}

export function initTabCoordinator(): void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return;
  }
  if (channel) return;
  tabId = crypto.randomUUID();
  initTabRegistry(tabId);
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (ev: MessageEvent<TabMessage>) => {
    const msg = ev.data;
    if (msg?.type === 'leader_ping' || msg?.type === 'leader_claim') {
      lastRemoteLeaderPing = msg.at;
      if (
        msg.type === 'leader_claim' &&
        msg.tabId !== tabId &&
        isPersistedLeader(tabId)
      ) {
        const remoteEpoch = readLeader()?.epoch ?? 0;
        const winner = resolveLeaderConflict(
          {
            tabId,
            epoch: getLeaderEpochFromStorage(),
            fencingToken: getLeaderFencingToken(),
          },
          {
            tabId: msg.tabId,
            epoch: remoteEpoch,
            fencingToken: msg.fencingToken,
          }
        );
        if (winner === 'remote') {
          invalidatePreviousOwner(
            tabId,
            getLeaderEpochFromStorage(),
            getLeaderFencingToken()
          );
          releaseLeader(tabId);
          recordLeaderChange();
          recordEpochChange();
        }
      }
    }
    if (msg?.type === 'leader_release' && msg.tabId !== tabId) {
      lastRemoteLeaderPing = 0;
      lastLeaderReleaseAt = Date.now();
    }
    if (msg?.type === 'flush_request' && isLeaderTab()) {
      void import('./queue').then(m => m.flushQueue());
    }
  };

  claimLeaderIfStale(tabId);
  const tick = (): void => {
    if (!isPersistedLeader(tabId) && !claimLeaderIfStale(tabId)) return;
    renewLeader(tabId);
    broadcastLeaderClaim();
  };
  tick();
  leaderHeartbeatId = setInterval(tick, LEADER_HEARTBEAT_MS);

  const onPageHide = (): void => {
    releaseLeaderAndBroadcast();
  };
  window.addEventListener('pagehide', onPageHide);
}

function releaseLeaderAndBroadcast(): void {
  if (!tabId) return;
  releaseLeader(tabId);
  channel?.postMessage({
    type: 'leader_release',
    tabId,
  } satisfies TabMessage);
  broadcastTabDead(tabId);
}

export function destroyTabCoordinator(): void {
  releaseLeaderAndBroadcast();
  if (leaderHeartbeatId) {
    clearInterval(leaderHeartbeatId);
    leaderHeartbeatId = null;
  }
  destroyTabRegistry();
  channel?.close();
  channel = null;
}

export function _testSetLeader(_value: boolean): void {
  /* compat testes legados — liderança via LS */
}
